import { CSSProperties, FC, JSX, ReactNode } from 'react';
import classNames from 'classnames';

import { PrismicRichText } from '@prismicio/react';
import { asText } from '@prismicio/helpers';

import { CtaButton, PrismicImageWithAlt } from '@/components';
import {
  ImageWithTextSideBySideSlice,
  ImageWithTextSideBySideSliceDefault,
  ImageWithTextSideBySideSliceWithCtaOnImageSide,
  ImageWithTextSideBySideSliceWithFeaturesList,
  ImageWithTextSideBySideSliceWithFlatImage,
  ImageWithTextSideBySideSliceWithSocialIcons,
  ImageWithTextSideBySideSliceWithWideSocialIcons,
} from '@/types';
import {
  getContentAlign,
  getContentVerticalAlign,
  HeadingAsParagraph,
  htmlSerializerWithProps,
  iwtsPrimaryFields,
  iwtsSecondaryFields,
  toCtaButtonPropsFromItem,
  withIwtsFitContent,
} from '@/utils';
import { resolveImageRadiusToken } from '../../theme/tokens';

import style from './components.module.scss';

const IWTS_SECTION_VARIATIONS = new Set([
  'withFeaturesList',
  'withSocialIcons',
  'withWideSocialIcons',
]);

const usesIwtsCtaSectionWrapper = (
  variation: string,
  hasPrimaryLabel: boolean,
): boolean => IWTS_SECTION_VARIATIONS.has(variation) && hasPrimaryLabel;

/** Width classes for a standalone IWTS CTA (not inside `.cta_section`). */
export const getIwtsStandaloneCtaClassName = ({
  isSingleButton = true,
  isFlatImage = false,
  isLink = false,
  fitContent = false,
}: {
  isSingleButton?: boolean;
  isFlatImage?: boolean;
  isLink?: boolean;
  fitContent?: boolean;
}): string | undefined =>
  classNames(style.cta, {
    [style.single_cta]: isSingleButton && !isLink && !fitContent,
    [style.single_cta_flat]: isFlatImage && !isLink && !fitContent,
    [style.single_cta_fit_content]: isSingleButton && fitContent && !isLink,
  });

export const getIwtsCtaSectionClassName = ({
  variation,
  hasSecondaryCta,
  fitContent,
}: {
  variation: string;
  hasSecondaryCta: boolean;
  fitContent: boolean;
}): string =>
  classNames(style.cta_section, {
    [style.cta_with_social_icons]:
      variation === 'withSocialIcons' || variation === 'withWideSocialIcons',
    [style.cta_section_with_two_buttons]:
      (variation === 'withFeaturesList' || variation === 'withSocialIcons') &&
      hasSecondaryCta &&
      !fitContent,
    [style.cta_section_with_single_button]:
      (!(variation === 'withFeaturesList' || variation === 'withSocialIcons') ||
        !hasSecondaryCta) &&
      !fitContent,
    [style.cta_section_fit_content]: fitContent,
  });

export const getIwtsIconTextOrderStyle = (alignText: string): CSSProperties =>
  ({
    '--icon-order': alignText === 'right' ? 0 : 1,
    '--text-order': alignText === 'right' ? 1 : 0,
  } as CSSProperties);

type IwtsContentVerticalLayout = 'block' | 'fullHeight';

export const getIwtsContentAlignStyle = ({
  contentHorizontalAlign,
  contentVerticalAlign,
  background,
  layout = 'block',
}: {
  contentHorizontalAlign?: string | null;
  contentVerticalAlign?: 'top' | 'center' | 'bottom' | null;
  background?: string;
  layout?: IwtsContentVerticalLayout;
}): CSSProperties => {
  const alignText = contentHorizontalAlign ?? 'center';
  const horizontalAlign = getContentAlign(
    contentHorizontalAlign as 'start' | 'left' | 'center' | 'end' | 'right',
  );
  const verticalAlign = getContentVerticalAlign(contentVerticalAlign);

  const verticalStyle =
    layout === 'fullHeight'
      ? { justifyContent: verticalAlign }
      : { alignSelf: verticalAlign };

  return {
    alignItems: horizontalAlign,
    textAlign: alignText,
    ...verticalStyle,
    ...(background ? { background } : {}),
    '--cta-align': horizontalAlign,
  } as unknown as CSSProperties;
};

export const DefaultComponentSection: FC<{
  slice: (
    | ImageWithTextSideBySideSliceWithSocialIcons
    | ImageWithTextSideBySideSliceWithWideSocialIcons
    | ImageWithTextSideBySideSliceWithFeaturesList
    | ImageWithTextSideBySideSliceDefault
    | ImageWithTextSideBySideSliceWithFlatImage
  ) &
    ImageWithTextSideBySideSlice;
  children?: ReactNode;
}> = ({ slice, children = null }) => {
  const {
    content_background_color,
    content_horizontal_align,
    content_vertical_align,
    content_full_width,
    description,
    description_color,
    heading,
    heading_color,
    image,
    icon_as_heading,
    icon_on_title,
    layout_direction,
    title,
    title_color,
  } = slice.primary;

  const icon_on_title_custom_width = (
    slice as ImageWithTextSideBySideSliceDefault
  )?.primary?.icon_on_title_custom_width;
  const icon_as_heading_custom_width = (
    slice as ImageWithTextSideBySideSliceDefault
  )?.primary?.icon_as_heading_custom_width;

  const layoutDirection = layout_direction ?? 'left';
  const alignText = content_horizontal_align ?? 'center';
  const imageCornerPreset =
    'image_corner_preset' in slice.primary
      ? slice.primary.image_corner_preset
      : 'brand';

  const imageMobile = image?.mobile?.url ? image.mobile : image;
  const hasContentBackground = !!content_background_color;

  return (
    <>
      <div
        className={classNames(style.body, {
          [style.body__left]: layoutDirection === 'left',
          [style.body__right]: layoutDirection === 'right',
          [style.body__full_left]:
            content_full_width && layoutDirection === 'left',
          [style.body__full_right]:
            content_full_width && layoutDirection === 'right',
        })}
      >
        <div
          className={classNames(
            style.content,
            {
              [style.content_with_background]: hasContentBackground,
            },
            hasContentBackground &&
              resolveImageRadiusToken(imageCornerPreset as string),
          )}
          style={getIwtsContentAlignStyle({
            background: content_background_color as string,
            contentHorizontalAlign: content_horizontal_align,
            contentVerticalAlign: content_vertical_align,
          })}
        >
          {(icon_as_heading?.url || asText(heading)) && (
            <div
              className={style.body__heading_container}
              style={getIwtsIconTextOrderStyle(alignText)}
            >
              {icon_as_heading?.url && (
                <figure
                  className={classNames(style.body__heading_icon, {
                    [style.body__heading_icon__custom_width]:
                      !!icon_as_heading_custom_width,
                  })}
                  style={{ maxWidth: `${icon_as_heading_custom_width}px` }}
                >
                  <PrismicImageWithAlt
                    defaultAlt="Decoration icon"
                    field={icon_as_heading}
                    style={{ objectFit: 'contain' }}
                  />
                </figure>
              )}
              <HeadingAsParagraph
                className={style.body__text_heading}
                color={heading_color as string}
                field={heading}
              />
            </div>
          )}
          {title && (
            <div
              className={style.body__title_container}
              style={getIwtsIconTextOrderStyle(alignText)}
            >
              <PrismicRichText
                components={htmlSerializerWithProps(
                  {
                    color: title_color,
                  },
                  style.body__title,
                )}
                field={title}
              />
              {icon_on_title?.url && alignText !== 'center' && (
                <figure
                  className={classNames(style.body__title_icon, {
                    [style.body__title_icon__custom_width]:
                      !!icon_on_title_custom_width,
                  })}
                  style={{ maxWidth: `${icon_on_title_custom_width}px` }}
                >
                  <PrismicImageWithAlt
                    defaultAlt="Decoration icon"
                    field={icon_on_title}
                    style={{
                      objectFit: 'contain',
                      maxWidth: `${icon_on_title_custom_width}px`,
                    }}
                  />
                </figure>
              )}
            </div>
          )}
          {icon_on_title?.url && alignText === 'center' && (
            <figure
              className={classNames(style.body__title_icon_centered, {
                [style.body__title_icon_centered__custom_width]:
                  !!icon_on_title_custom_width,
              })}
              style={{ maxWidth: `${icon_on_title_custom_width}px` }}
            >
              <PrismicImageWithAlt
                defaultAlt="Decoration icon"
                field={icon_on_title}
                style={{
                  objectFit: 'contain',
                }}
              />
            </figure>
          )}
          {description && (
            <div className={style.body__description_text}>
              <PrismicRichText
                components={htmlSerializerWithProps({
                  color: description_color,
                })}
                field={description}
              />
            </div>
          )}
          {children}
          <CtaSection
            isFlatImage={slice.variation === 'withFlatImage'}
            slice={slice}
          />
        </div>
      </div>
      <figure
        className={classNames(
          style.body__figure,
          resolveImageRadiusToken(imageCornerPreset as string),
          {
            [style.body__figure_placeholder]: !image?.url,
            [style.body__figure__with_flat_image]:
              slice.variation === 'withFlatImage',
            [style.body__figure__full_width_left]:
              !!content_full_width && layoutDirection === 'left',
            [style.body__figure__full_width_right]:
              !!content_full_width && layoutDirection === 'right',
          },
        )}
      >
        {image?.url && (
          <PrismicImageWithAlt
            className={style.body__figure_desktop}
            field={image}
            fill
            showPlaceholderIfEmpty
            style={{ objectFit: 'cover' }}
          />
        )}

        {imageMobile?.url && (
          <PrismicImageWithAlt
            className={style.body__figure_mobile}
            field={imageMobile}
            fill
            showPlaceholderIfEmpty
            style={{ objectFit: 'cover' }}
          />
        )}
      </figure>
    </>
  );
};

export const CtaSection = ({
  slice,
  isFlatImage = false,
}: {
  slice: (
    | ImageWithTextSideBySideSliceWithFeaturesList
    | ImageWithTextSideBySideSliceDefault
    | ImageWithTextSideBySideSliceWithFlatImage
    | ImageWithTextSideBySideSliceWithCtaOnImageSide
    | ImageWithTextSideBySideSliceWithSocialIcons
    | ImageWithTextSideBySideSliceWithWideSocialIcons
  ) &
    ImageWithTextSideBySideSlice;
  isFlatImage?: boolean;
}): JSX.Element => {
  const { primary } = slice;
  const primaryCta = withIwtsFitContent(
    toCtaButtonPropsFromItem(primary, iwtsPrimaryFields, 'primary'),
    primary,
    slice.variation,
  );
  const hasSecondaryCta =
    (slice.variation === 'withFeaturesList' ||
      slice.variation === 'withSocialIcons' ||
      slice.variation === 'withWideSocialIcons') &&
    'secondary_cta_text' in primary &&
    !!primary.secondary_cta_text;

  const secondaryCta =
    hasSecondaryCta && 'secondary_cta_text' in primary
      ? withIwtsFitContent(
          toCtaButtonPropsFromItem(primary, iwtsSecondaryFields, 'secondary'),
          primary,
          slice.variation,
        )
      : null;

  if (slice.variation !== 'withFeaturesList' && !slice.primary.cta_text) {
    return <></>;
  }

  if (!primaryCta && !secondaryCta) {
    return <></>;
  }

  const useSectionWrapper = usesIwtsCtaSectionWrapper(
    slice.variation,
    Boolean(slice.primary.cta_text),
  );

  const fitContent = Boolean(primaryCta?.fitContent);

  const buttons = (
    <>
      {primaryCta && (
        <CtaButton
          {...primaryCta}
          className={
            useSectionWrapper
              ? style.cta
              : getIwtsStandaloneCtaClassName({
                  isFlatImage,
                  isLink: primaryCta.variant === 'link',
                  fitContent,
                })
          }
          {...(fitContent ? { fitContent: true } : { fitContent: false })}
        />
      )}
      {hasSecondaryCta && secondaryCta && (
        <CtaButton
          {...secondaryCta}
          className={style.cta}
          {...(fitContent ? { fitContent: true } : { fitContent: false })}
        />
      )}
    </>
  );

  if (!useSectionWrapper) {
    return buttons;
  }

  return (
    <div
      className={getIwtsCtaSectionClassName({
        variation: slice.variation,
        hasSecondaryCta,
        fitContent,
      })}
      style={
        {
          '--cta-section-align': getContentAlign(
            slice.primary.content_horizontal_align,
          ),
        } as CSSProperties
      }
    >
      {buttons}
    </div>
  );
};

/** @deprecated Prefer `getContentAlign` from `@/utils` for horizontal alignment. */
export {
  getContentAlign as textAlign,
  getContentAlign,
  getContentVerticalAlign,
};

export const TextSlice = ({
  text,
  color,
  maxWidth,
  centeredText,
  className,
}: {
  text: any;
  color: string;
  maxWidth?: string;
  centeredText?: boolean;
  className?: string;
}) => {
  if (!asText(text)) {
    return null;
  }

  const richText = (
    <PrismicRichText
      components={htmlSerializerWithProps({
        color: color,
        maxWidth: maxWidth,
        textAlign: centeredText && 'center',
      })}
      field={text}
    />
  );

  if (className) {
    return <div className={className}>{richText}</div>;
  }

  return richText;
};
